/* ============================================================
   SINGSPACE ADMIN — BOOKINGS PAGE (CLEAN CODE REFACTORED)
   ============================================================ */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { BookingDetailsDrawer } from '../../components/BookingDetailsDrawer';
import { BookingFiltersBar } from '../../components/bookings/BookingFiltersBar';
import { BookingPagination } from '../../components/bookings/BookingPagination';
import { BookingStatsCards } from '../../components/bookings/BookingStatsCards';
import { BookingTable } from '../../components/bookings/BookingTable';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { getDocumentLabel } from '../../constants/booking-details.constants';
import { useBookingsAdmin } from '../../hooks/useBookingsAdmin';
import { api } from '../../services/api';
import { Booking } from '../../types/booking.types';
import { getDurationLabel } from '../../utils/capacity-brackets.utils';

export function BookingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBookingId = searchParams.get('booking');
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const {
    bookings, // déjà paginés côté serveur
    loading,
    error,
    pagination,
    stats,
    statusFilter,
    typeFilter,
    dateFilter,
    searchQuery,
    hasActiveFilters,
    updateFilter,
    updateSearch,
    resetFilters,
    setPage,
    refresh,
  } = useBookingsAdmin();

  // Charger les détails complets de la réservation depuis l'API
  useEffect(() => {
    if (!selectedBookingId) {
      setSelectedBooking(null);
      setIsLoadingDetails(false);
      return;
    }

    // ✅ FIX 1: Réinitialiser immédiatement ET indiquer le chargement
    setSelectedBooking(null);
    setIsLoadingDetails(true);

    const fetchBookingDetails = async () => {
      try {
        const booking = bookings.find((b) => b.ref === selectedBookingId);
        if (!booking) {
          setIsLoadingDetails(false);
          return;
        }

        const { data } = await api.get(`/bookings/admin/${booking.id}`);
        if (data.success) {
          setSelectedBooking(transformBookingDetails(data.data));
        }
      } catch (err) {
        console.error('[BookingsPage] Error fetching booking details:', err);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchBookingDetails();
  }, [selectedBookingId, bookings]);

  const transformBookingDetails = (data: any): any => {
    // 🔍 DEBUG: Logger les données reçues de l'API
    console.log('[BookingsPage] 📦 Données API reçues:', {
      roomLayoutDetails: data.roomLayoutDetails,
      specificDetails: data.specificDetails,
      roomLayoutId: data.specificDetails?.roomLayoutId,
    });

    // Extraire les détails spécifiques du JSON
    const specificDetails = (data.specificDetails as Record<string, any>) || {};

    // Déterminer la disposition et les équipements selon le type d'espace
    const layout = specificDetails.roomLayout || specificDetails.layout || 'Standard';
    const equipment = specificDetails.equipment || specificDetails.includedEquipment || [];
    const services = specificDetails.services || [];
    const cateringOptions = specificDetails.cateringOptions || [];

    // --- Transformer specificDetails pour un affichage humain ---
    // Champs déjà affichés ailleurs (Client, Espace, Horaires, etc.) — à exclure
    const skipKeys = new Set([
      // Infos client → affiché dans la section Client
      'firstName',
      'lastName',
      'email',
      'phone',
      'companyName',
      'companyPhone',
      'companyEmail',
      'companyWebsite',
      // Configuration → affiché dans Espace & Configuration
      'roomLayout',
      'roomLayoutId',
      'roomLayoutName',
      'roomLayoutImageUrl',
      'roomLayoutDescription',
      'roomLayoutOther',
      'roomLayoutOtherName',
      'layout',
      'roomLayoutId',
      'services',
      'cateringOptions',
      'equipment',
      'includedEquipment',
      'participantCount',
      // Horaires → affiché dans Horaires
      'startTime',
      'endTime',
      'durationType',
      // Documents → affiché dans l'onglet Documents
      'documents',
      // Motif → affiché dans Motif/Note
      'bookingReason',
      // Technique/système — jamais affiché
      'acceptedDisclaimer',
      'capacityBracketId',
      // Type d'événement → déjà affiché dans les détails principaux si eventType existe
      'eventType',
      'eventTypeOther',
    ]);

    // Labels français pour les clés connues du formulaire
    const fieldLabels: Record<string, string> = {
      equipmentIds: 'Équipements sélectionnés',
      // Restauration
      hasMealBreak: 'Pause repas',
      wantsMealBreak: 'Pause repas',
      wantsCoffeeBreak: 'Pause café',
      hasExternalCaterer: 'Traiteur externe',
      mealCatererType: 'Type de traiteur',
      mealSingDetails: 'Détails restauration SING',
      mealExternalName: 'Nom du traiteur externe',
      mealExternalEmail: 'Email du traiteur externe',
      mealExternalPhone: 'Téléphone du traiteur externe',
      catererContact: 'Contact du traiteur',
      // Devis / options
      urgentQuote: 'Devis urgent',
      daysCount: 'Nombre de jours',
      startDate: 'Date de début',
      endDate: 'Date de fin',
      startTime: 'Heure de début',
      endTime: 'Heure de fin',
      // Objectifs & notes
      eventObjectives: "Objectifs de l'événement",
      additionalNotes: 'Notes additionnelles',
      setupRequirements: "Besoins d'installation",
      technicalNeeds: 'Besoins techniques',
      accessibilityNeeds: "Besoins d'accessibilité",
      specialRequests: 'Demandes spéciales',
      organizationType: "Type d'organisation",
      expectedAudience: 'Audience attendue',
      budgetRange: 'Fourchette budgétaire',
      preferredDate: 'Date préférée',
      alternativeDate: 'Date alternative',
      contactPreference: 'Préférence de contact',
      customerNotes: 'Notes du client',
    };

    // Traductions des valeurs d'enum connues
    const enumTranslations: Record<string, Record<string, string>> = {
      eventType: {
        CONFERENCE: 'Conférence',
        MEETING: 'Réunion',
        WORKSHOP: 'Atelier',
        SEMINAR: 'Séminaire',
        TRAINING: 'Formation',
        RECEPTION: 'Réception',
        CORPORATE_EVENT: "Événement d'entreprise",
        PRIVATE_EVENT: 'Événement privé',
        OTHER: 'Autre',
      },
      mealCatererType: {
        EXTERNAL: 'Traiteur externe',
        SING: 'Traiteur SING',
      },
      durationType: {
        FORFAIT: 'Forfait',
        HALF_DAY: 'Demi-journée',
        FULL_DAY: 'Journée',
        MONTH: 'Mois',
      },
    };

    // Transformer les valeurs pour affichage lisible
    const displaySpecificDetails: Record<string, string> = {};
    for (const [key, value] of Object.entries(specificDetails)) {
      if (skipKeys.has(key)) continue;
      // Ignorer les valeurs nulles, undefined ou tableaux vides
      if (value === null || value === undefined) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      // Ignorer les chaînes vides
      if (typeof value === 'string' && value.trim() === '') continue;

      const label =
        fieldLabels[key] ||
        key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (s) => s.toUpperCase())
          .trim();

      if (key === 'equipmentIds' && Array.isArray(value)) {
        // Utiliser les noms résolus depuis le backend (resolvedEquipmentNames)
        // Filtrer les CUIDs bruts au cas où le backend n'aurait pas pu résoudre
        const isCuid = (s: string) => /^c[a-z0-9]{20,}$/i.test(s);
        const names = Array.isArray(data.resolvedEquipmentNames)
          ? data.resolvedEquipmentNames.filter((n: string) => n && !isCuid(n))
          : [];
        if (names.length > 0) {
          displaySpecificDetails[label] = names.join(', ');
        }
        // Si pas de noms résolus, on n'affiche PAS les UUIDs bruts
      } else if (typeof value === 'boolean') {
        displaySpecificDetails[label] = value ? 'Oui' : 'Non';
      } else if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        // Formater les dates ISO en format français complet : "Vendredi, 05 Mai 2026"
        const date = new Date(value);
        const options: Intl.DateTimeFormatOptions = {
          weekday: 'long',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        };
        const formatted = date.toLocaleDateString('fr-FR', options);
        // Capitaliser le premier caractère
        displaySpecificDetails[label] = formatted.charAt(0).toUpperCase() + formatted.slice(1);
      } else if (typeof value === 'string' && enumTranslations[key]?.[value]) {
        displaySpecificDetails[label] = enumTranslations[key][value];
      } else if (Array.isArray(value)) {
        const items = value
          .map((item: any) => {
            if (typeof item === 'object' && item !== null) {
              return item.name || item.label || item.filename || null;
            }
            return String(item);
          })
          .filter(Boolean);
        if (items.length > 0) {
          displaySpecificDetails[label] = items.join(', ');
        }
      } else if (typeof value === 'object' && value !== null) {
        // Ignorer les objets complexes (documents inline, etc.)
        const objDisplay = (value as any).name || (value as any).label;
        if (objDisplay) {
          displaySpecificDetails[label] = objDisplay;
        }
      } else {
        displaySpecificDetails[label] = String(value);
      }
    }

    // --- Mapper les documents Prisma (BookingDocument) vers le format du Drawer ---
    const getDocumentTypeFromKey = (
      documentKey: string,
    ): 'invoice' | 'quote' | 'confirmation' | 'contract' | 'id' | 'purchase_order' => {
      // Mapper les clés de documents vers des types visuels
      const keyToTypeMap: Record<string, 'invoice' | 'quote' | 'confirmation' | 'contract' | 'id' | 'purchase_order'> =
        {
          id_card: 'id',
          identity: 'id',
          passport: 'id',
          business_license: 'contract',
          kbis: 'contract',
          company_registration: 'contract',
          tax_certificate: 'invoice',
          proof_of_address: 'confirmation',
          insurance: 'contract',
          purchase_order: 'purchase_order',
          rib: 'confirmation',
          bank: 'confirmation',
          event_program: 'confirmation',
        };

      return keyToTypeMap[documentKey.toLowerCase()] || 'confirmation';
    };

    // Récupérer les labels configurés par l'admin
    const documentLabels = (data.documentLabels as Record<string, string>) || {};

    // Convertir un data URI (base64) en Blob URL téléchargeable
    const dataUriToBlobUrl = (dataUri: string): string => {
      try {
        const [header, base64Data] = dataUri.split(',');
        const mimeMatch = header.match(/data:([^;]+)/);
        const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
        const byteChars = atob(base64Data);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteArray[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: mime });
        return URL.createObjectURL(blob);
      } catch {
        return '';
      }
    };

    // Résoudre l'URL d'un document : si c'est du base64, créer un Blob URL
    const resolveDocUrl = (rawUrl: string | undefined): string => {
      if (!rawUrl) return '';
      if (rawUrl.startsWith('data:')) return dataUriToBlobUrl(rawUrl);
      return rawUrl;
    };

    const prismaDocuments = Array.isArray(data.documents)
      ? data.documents.map((doc: any) => ({
          name: getDocumentLabel(doc.documentKey, documentLabels) || 'Document',
          fileName: doc.fileName || doc.documentKey || 'document.pdf',
          type: getDocumentTypeFromKey(doc.documentKey || ''),
          date: doc.uploadedAt
            ? new Date(doc.uploadedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
            : new Date(data.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }),
          url: resolveDocUrl(doc.fileUrl),
          fileSize: doc.fileSize,
          mimeType: doc.mimeType,
        }))
      : [];

    // Ajouter les documents du formulaire (specificDetails.documents)
    // Structure réelle : { id_card: [{name, size, type, data}], business_license: [...] }
    const formDocuments: typeof prismaDocuments = [];
    if (
      specificDetails.documents &&
      typeof specificDetails.documents === 'object' &&
      !Array.isArray(specificDetails.documents)
    ) {
      // C'est un dict catégorisé : { id_card: [...], business_license: [...] }
      for (const [category, docs] of Object.entries(specificDetails.documents)) {
        if (!Array.isArray(docs)) continue;
        const categoryLabel = getDocumentLabel(category, documentLabels);
        docs.forEach((doc: any) => {
          if (typeof doc !== 'object' || doc === null) return;
          formDocuments.push({
            name: categoryLabel,
            fileName: doc.name || doc.filename || categoryLabel,
            type: getDocumentTypeFromKey(category),
            date: new Date(data.createdAt).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            }),
            url: resolveDocUrl(doc.data || doc.url),
            fileSize: doc.size || doc.fileSize,
            mimeType: doc.type || doc.mimeType,
          });
        });
      }
    }

    // Dédoublonnage : si on a un document avec le même fichier (taille similaire ou mimeType + name), on garde l'entrée Prisma prioritaire
    const allDocsMap = new Map();
    // On insère formDocuments en premier
    formDocuments.forEach((doc: any) => {
      const key = `${doc.name}_${doc.fileSize || 0}`;
      allDocsMap.set(key, doc);
    });
    // On écrase avec prismaDocuments (la source de vérité base de données)
    prismaDocuments.forEach((doc: any) => {
      const key = `${doc.name}_${doc.fileSize || 0}`;
      allDocsMap.set(key, doc);
    });

    const allDocuments = Array.from(allDocsMap.values());

    // --- Construire la timeline enrichie avec les transactions ---
    const timeline: { date: string; event: string; type: 'info' | 'success' | 'warning' | 'error' }[] = [
      {
        date: new Date(data.createdAt).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        event: 'Réservation créée',
        type: 'info',
      },
    ];

    // Ajouter les événements de transaction
    if (Array.isArray(data.transactions)) {
      const txStatusLabels: Record<string, { label: string; type: 'info' | 'success' | 'warning' | 'error' }> = {
        INITIATED: { label: 'Paiement initié', type: 'info' },
        PENDING: { label: 'Paiement en attente', type: 'warning' },
        SUCCESS: { label: 'Paiement réussi', type: 'success' },
        COMPLETED: { label: 'Paiement réussi', type: 'success' },
        PAID: { label: 'Paiement réussi', type: 'success' },
        FAILED: { label: 'Paiement échoué', type: 'error' },
        CANCELLED: { label: 'Paiement annulé', type: 'error' },
        REFUNDED: { label: 'Paiement remboursé', type: 'warning' },
      };

      data.transactions.forEach((tx: any) => {
        const txConfig = txStatusLabels[tx.status] || { label: `Transaction ${tx.status}`, type: 'info' as const };
        const amount = tx.amount
          ? ` — ${new Intl.NumberFormat('fr-FR').format(tx.amount)} ${tx.currency || 'XAF'}`
          : '';
        timeline.push({
          date: new Date(tx.updatedAt || tx.createdAt).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
          event: `${txConfig.label}${amount} (Réf: ${tx.reference})`,
          type: txConfig.type,
        });
      });
    }

    // Événement de validation
    if (data.validatedAt) {
      const validatorName = data.validatedBy?.name || 'Admin';
      timeline.push({
        date: new Date(data.validatedAt).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        event: `Réservation validée par ${validatorName}`,
        type: 'success',
      });
    }

    // Trier la timeline par date chronologique
    timeline.sort((a, b) => {
      const dateA = new Date(a.date.split(' à ')[0].split('/').reverse().join('-')).getTime() || 0;
      const dateB = new Date(b.date.split(' à ')[0].split('/').reverse().join('-')).getTime() || 0;
      return dateA - dateB;
    });

    // ✅ FIX 3: Déterminer le statut de paiement basé sur la source de vérité (backend)
    const paymentStatus = (() => {
      // Statuts backend qui indiquent un paiement complet
      const paidStatuses = ['PAID', 'COMPLETED', 'CONFIRMED'];
      const failedStatuses = ['CANCELLED', 'REJECTED'];

      // ⚠️ RÈGLE CRITIQUE: URGENT et OVERDUE signifient que SEULS les frais d'urgence sont payés
      // Le montant TOTAL n'est PAS encore payé
      if (data.status === 'URGENT' || data.status === 'OVERDUE') {
        return 'PENDING' as const;
      }

      // 1. Vérifier le statut de la réservation (source de vérité principale)
      if (paidStatuses.includes(data.status)) return 'PAID' as const;
      if (failedStatuses.includes(data.status)) return 'FAILED' as const;

      // 2. Vérifier les transactions (source de vérité secondaire)
      if (Array.isArray(data.transactions) && data.transactions.length > 0) {
        // Vérifier si toutes les transactions sont complètes
        const hasSuccessfulTransaction = data.transactions.some(
          (tx: any) => tx.status === 'SUCCESS' || tx.status === 'COMPLETED' || tx.status === 'PAID',
        );

        if (hasSuccessfulTransaction) return 'PAID' as const;

        // Vérifier si toutes les transactions ont échoué
        const allFailed = data.transactions.every((tx: any) => tx.status === 'FAILED' || tx.status === 'CANCELLED');

        if (allFailed) return 'FAILED' as const;
      }

      // 3. Par défaut, en attente
      return 'PENDING' as const;
    })();

    // Déterminer la méthode de paiement
    const paymentMethod = (() => {
      if (Array.isArray(data.transactions) && data.transactions.length > 0) {
        const lastTx = data.transactions[data.transactions.length - 1];
        if (lastTx.type === 'MOBILE_MONEY') return 'Mobile Money';
        if (lastTx.type === 'CARD') return 'Carte bancaire';
        if (lastTx.type === 'BANK_TRANSFER') return 'Virement bancaire';
        return lastTx.type || 'Non spécifié';
      }
      return 'Non spécifié';
    })();

    return {
      id: String(data.id),
      ref: data.reference,
      client: {
        name: data.customerName,
        email: data.customerEmail,
        phone: data.customerPhone,
        company: data.companyName,
      },
      space: {
        name: data.spaceType?.name || 'Espace',
        type: data.spaceType?.category || data.spaceType?.name || 'Espace',
        capacity: data.participantCount || 1,
        configuration:
          layout !== 'Standard' || data.roomLayoutDetails
            ? {
                layout: data.roomLayoutDetails?.name || layout,
                layoutImageUrl: data.roomLayoutDetails?.imageUrl || undefined,
                layoutDescription: data.roomLayoutDetails?.description || undefined,
                configuredCapacity: data.participantCount || 1,
                includedEquipment: Array.isArray(equipment) ? equipment : [],
              }
            : undefined,
      },
      // Ajouter les services si présents
      services:
        services.length > 0 || cateringOptions.length > 0
          ? [
              ...services.map((s: any, idx: number) => ({
                id: String(idx + 1),
                category: 'equipment' as const,
                name: s.name || s,
                unitPrice: s.price || 0,
                quantity: s.quantity || 1,
                totalPrice: (s.price || 0) * (s.quantity || 1),
                details: s.details,
              })),
              ...cateringOptions.map((c: any, idx: number) => ({
                id: String(services.length + idx + 1),
                category: 'catering' as const,
                name: c.name || c,
                unitPrice: c.price || 0,
                quantity: c.quantity || data.participantCount || 1,
                totalPrice: (c.price || 0) * (c.quantity || data.participantCount || 1),
                details: c.details,
              })),
            ]
          : undefined,
      details: {
        startDate: new Date(data.startDate).toLocaleDateString('fr-FR'),
        endDate: data.endDate
          ? new Date(data.endDate).toLocaleDateString('fr-FR')
          : new Date(data.startDate).toLocaleDateString('fr-FR'),
        startTime: data.startTime || '09:00',
        endTime: data.endTime || '18:00',
        duration: specificDetails.durationType
          ? getDurationLabel(specificDetails.durationType)
          : data.endDate
            ? 'Multi-jours'
            : '1 Journée',
        attendees: data.participantCount || 1,
        purpose: specificDetails.bookingReason || data.eventObjectives || data.eventType,
        specificDetails: Object.keys(displaySpecificDetails).length > 0 ? displaySpecificDetails : undefined,
      },
      financials: {
        amount: data.totalAmount,
        currency: data.currency || 'XAF',
        paymentStatus,
        paymentMethod,
        hasQuote: data.workflow === 'QUOTE_REQUIRED',
        // Utiliser le vrai breakdown stocké dans metadata.priceBreakdown (moteur de prix)
        // Fallback sur baseAmount/optionsAmount si indisponible (anciennes réservations)
        breakdown: (() => {
          const metaBreakdown = (data.metadata as any)?.priceBreakdown;
          if (Array.isArray(metaBreakdown) && metaBreakdown.length > 0) {
            console.log('[BookingsPage] ✅ Utilisation de metadata.priceBreakdown:', metaBreakdown);
            return metaBreakdown as { label: string; amount: number }[];
          }
          // Fallback pour anciennes réservations sans metadata.priceBreakdown
          console.warn('[BookingsPage] ⚠️ metadata.priceBreakdown absent, fallback sur baseAmount/optionsAmount');
          return [
            { label: 'Montant de base', amount: data.baseAmount || data.totalAmount },
            ...(data.optionsAmount > 0 ? [{ label: 'Options et services', amount: data.optionsAmount }] : []),
            ...(data.totalAmount && data.baseAmount && data.totalAmount !== data.baseAmount && !data.optionsAmount
              ? [{ label: 'Ajustements', amount: data.totalAmount - data.baseAmount }]
              : []),
          ];
        })(),
        ...(data.invoice ? { invoiceNumber: data.invoice.invoiceNumber || data.invoice.reference } : {}),
      },
      status: data.status,
      isUrgent: data.isUrgent || false,
      urgentPaidAt: data.urgentPaidAt ? new Date(data.urgentPaidAt).toISOString() : null,
      createdAt: new Date(data.createdAt).toLocaleDateString('fr-FR'),
      timeline,
      documents: allDocuments,
      notes: data.adminNotes
        ? [{ author: 'Admin', date: new Date(data.createdAt).toLocaleDateString('fr-FR'), text: data.adminNotes }]
        : [],
    };
  };

  const openBookingDrawer = (booking: Booking) => {
    setSearchParams((prev) => {
      prev.set('booking', booking.ref);
      return prev;
    });
  };

  const closeBookingDrawer = () => {
    setSearchParams((prev) => {
      prev.delete('booking');
      return prev;
    });
  };

  const handleBookingAction = async (action: string, id: string) => {
    console.log(`Action [${action}] on booking ID: ${id}`);

    try {
      if (action === 'generate-invoice') {
        // Afficher un toast de chargement
        const loadingToast = toast.loading('Génération de la facture en cours...', {
          description: 'Veuillez patienter, cela peut prendre quelques secondes',
        });

        try {
          const { generateInvoice } = await import('../../features/invoices/api/invoicesApi');
          const result = await generateInvoice(id);

          // Fermer le toast de chargement
          toast.dismiss(loadingToast);

          if (result.success) {
            toast.success('Facture générée avec succès !', {
              description: 'Vous pouvez la consulter dans la page Facturation.',
              duration: 5000,
            });
            refresh();
            closeBookingDrawer();
          } else {
            toast.error('Erreur lors de la génération', {
              description: result.error || 'Impossible de générer la facture',
              duration: 6000,
            });
          }
        } catch (err: any) {
          toast.dismiss(loadingToast);
          toast.error('Erreur lors de la génération', {
            description: err.message || 'Une erreur inattendue est survenue',
            duration: 6000,
          });
        }
        return;
      }

      if (action === 'confirm-payment') {
        const loadingToast = toast.loading('Confirmation du paiement...');

        try {
          const { apiService } = await import('../../services/api');
          const res = await apiService.confirmPayment(id);

          toast.dismiss(loadingToast);

          if (res.data.success) {
            toast.success('Paiement confirmé avec succès', {
              duration: 4000,
            });
            refresh();
          } else {
            toast.error('Erreur lors de la confirmation', {
              description: res.data.error || 'Impossible de confirmer le paiement',
            });
          }
        } catch (err: any) {
          toast.dismiss(loadingToast);
          toast.error('Erreur lors de la confirmation', {
            description: err.message || 'Une erreur est survenue',
          });
        }
      } else if (action === 'quote') {
        // Rediriger vers la page des devis pour cette réservation
        window.location.href = `/quotes?search=${selectedBooking?.ref || ''}`;
      } else if (action === 'accept') {
        const loadingToast = toast.loading('Acceptation de la réservation...');

        try {
          const { apiService } = await import('../../services/api');
          await apiService.updateBookingStatus(id, 'CONFIRMED');

          toast.dismiss(loadingToast);
          toast.success('Réservation acceptée', {
            duration: 4000,
          });
          refresh();
        } catch (err: any) {
          toast.dismiss(loadingToast);
          toast.error("Erreur lors de l'acceptation", {
            description: err.message || 'Une erreur est survenue',
          });
        }
      } else if (action === 'reject') {
        const loadingToast = toast.loading('Refus de la réservation...');

        try {
          const { apiService } = await import('../../services/api');
          await apiService.updateBookingStatus(id, 'REJECTED');

          toast.dismiss(loadingToast);
          toast.success('Réservation refusée', {
            duration: 4000,
          });
          refresh();
        } catch (err: any) {
          toast.dismiss(loadingToast);
          toast.error('Erreur lors du refus', {
            description: err.message || 'Une erreur est survenue',
          });
        }
      }
    } catch (err) {
      console.error('Action failed:', err);
      toast.error('Une erreur est survenue', {
        description: "Impossible d'effectuer cette action",
      });
    }

    closeBookingDrawer();
  };

  // Premier chargement : afficher un spinner plein écran
  if (loading && bookings.length === 0) {
    return <LoadingSpinner message="Chargement des réservations" />;
  }

  if (error) {
    return (
      <div className="space-y-6 pb-12">
        <PageHeader />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium mb-2">Erreur lors du chargement</p>
          <p className="text-red-500 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <PageHeader />

      <BookingStatsCards stats={stats} />

      <BookingFiltersBar
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        dateFilter={dateFilter}
        paymentFilter="all" // TODO: Ajouter filtre paiement dans l'API
        totalResults={pagination.total}
        filteredResults={bookings.length}
        hasActiveFilters={hasActiveFilters}
        onSearchChange={updateSearch}
        onFilterChange={updateFilter}
        onResetFilters={resetFilters}
      />

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden relative">
        {/* Overlay de chargement pour les changements de page/filtres */}
        {loading && bookings.length > 0 && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-xl z-10">
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-lg border border-slate-200">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-[#00758d]" />
              <span className="text-sm text-slate-600 font-medium">Chargement...</span>
            </div>
          </div>
        )}

        <BookingTable bookings={bookings} onBookingClick={openBookingDrawer} onResetFilters={resetFilters} />

        <BookingPagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalResults={pagination.total}
          onPageChange={setPage}
        />
      </div>

      <BookingDetailsDrawer
        isOpen={!!selectedBookingId}
        onClose={closeBookingDrawer}
        booking={isLoadingDetails ? null : selectedBooking}
        onAction={handleBookingAction}
      />
    </div>
  );
}

function PageHeader() {
  return (
    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Réservations</h1>
        <p className="text-slate-500 mt-1">Gérez et suivez toutes les réservations</p>
      </div>
    </div>
  );
}
